import React, { useRef, useState } from 'react';
import { products } from './Products';
import { hairProducts } from './HairPriceList';
import { useCart } from './CartContext';
import { useNavigate } from 'react-router-dom';

const FREE_SHIPPING_THRESHOLD = 5200;
const SHIPPING_FEE = 200;

const CheckoutPage = () => {
  const { cart, updateCartItem, removeFromCart, clearCart } = useCart();
  const navigate = useNavigate();
  const allProducts = [...products, ...hairProducts];

  const formRef = useRef(null);
  const cartInputRef = useRef(null);
  const [success, setSuccess] = useState(false);

  // NEW: terms acceptance state
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  // NEW: delivery method (delivery | collection)
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');

  const subtotal = cart.reduce((sum, item) => {
    const prod = allProducts.find(
      (p) => String(p.id) === String(item.productId)
    );
    return sum + (prod ? prod.price * item.quantity : 0);
  }, 0);

  const shipping =
    deliveryMethod === 'collection'
      ? 0
      : subtotal >= FREE_SHIPPING_THRESHOLD
      ? 0
      : SHIPPING_FEE;

  const total = subtotal + shipping;

  const getCartSummary = () => {
    if (cart.length === 0) return 'Cart empty';
    return cart
      .map((item) => {
        const prod = allProducts.find(
          (p) => String(p.id) === String(item.productId)
        );
        if (!prod) return '';
        return `${prod.title || prod.name} x${item.quantity} (R${
          prod.price * item.quantity
        })`;
      })
      .join('; ');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (cartInputRef.current) {
      cartInputRef.current.value = getCartSummary();
    }

    const formEl = e.target;
    const formData = new FormData(formEl);
    // include delivery method in form submission
    formData.append('delivery_method', deliveryMethod);
    const body = new URLSearchParams(formData).toString();

    try {
      const res = await fetch('https://api.staticforms.dev/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      if (res.ok) {
        setSuccess(true);
        // optional:
        // formEl.reset();
        // clearCart();
      } else {
        alert('There was a problem submitting the form. Please try again.');
      }
    } catch (err) {
      console.error(err);
      alert('There was a problem submitting the form. Please try again.');
    }
  };

  // NEW: handle click on Pay Now, enforce terms acceptance and delivery form submission
  const handlePayNowClick = (e) => {
    if (!success) {
      e.preventDefault();
      alert('Please submit your delivery information first.');
      const formElement = document.querySelector('form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    if (!acceptedTerms) {
      e.preventDefault();
      setShowTermsError(true);
      const el = document.getElementById('terms-checkbox-wrapper');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    setShowTermsError(false);
  };

  return (
    <section style={{ background: '#ffffff' }} className="py-4">
      <div className="container">
        <div className="row gx-3 gy-4 justify-content-center">
          {/* RIGHT: Order summary */}
          <div className="col-12 col-md-5 order-1 order-md-1">
            <div
              className="card border-0 shadow-sm rounded-4 p-3"
              style={{ background: '#fff' }}
            >
              <h3
                className="fw-bold mb-3"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: '#2c2c2c',
                  fontSize: '1.06rem',
                }}
              >
                Order Summary
              </h3>

              {cart.length === 0 && (
                <div className="text-muted" style={{ fontSize: '.97em' }}>
                  Your cart is empty.
                </div>
              )}

              {cart.map((item) => {
                const prod = allProducts.find(
                  (p) => String(p.id) === String(item.productId)
                );
                if (!prod) return null;
                return (
                  <div
                    key={item.productId}
                    className="d-flex align-items-center justify-content-between py-2 border-bottom"
                    style={{ borderColor: '#f5e4b8' }}
                  >
                    <div>
                      <div
                        className="fw-bold"
                        style={{ fontSize: '.98em', color: '#2c2c2c' }}
                      >
                        {prod.name || prod.title}
                      </div>
                      <div
                        className="small text-muted"
                        style={{ fontSize: '.93em' }}
                      >
                        Qty{' '}
                        <input
                          type="number"
                          min={1}
                          max={10}
                          value={item.quantity}
                          onChange={(e) =>
                            updateCartItem(
                              item.productId,
                              Number(e.target.value)
                            )
                          }
                          className="form-control d-inline-block ms-2"
                          style={{
                            width: 42,
                            display: 'inline-block',
                            fontSize: '.95em',
                          }}
                        />
                      </div>
                      <div
                        style={{
                          fontWeight: 500,
                          color: '#2c2c2c',
                          fontSize: '.98em',
                        }}
                      >
                        R {(prod.price * item.quantity).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.productId)}
                      className="btn rounded-pill fw-bold"
                      style={{
                        background: '#beae89',
                        color: '#fff',
                        minWidth: 100,
                        fontSize: '.98em',
                        paddingLeft: 18,
                        paddingRight: 18,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        border: 'none',
                        boxShadow: '0 2px 7px #baa56012',
                      }}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}

              {/* NEW: Delivery vs Collection toggle */}
              <div
                className="mt-3 p-2 rounded-3"
                style={{
                  border: '1px solid #f5e4b8',
                  background: '#ffffff',
                  fontSize: '.93em',
                }}
              >
                <div className="fw-semibold mb-1" style={{ color: '#6b4b13' }}>
                  Fulfilment Method
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    onClick={() => setDeliveryMethod('delivery')}
                    className="btn btn-sm fw-bold flex-grow-1"
                    style={{
                      borderRadius: 999,
                      background:
                        deliveryMethod === 'delivery'
                          ? 'linear-gradient(90deg,#d4c4a8,#c4a15a)'
                          : '#f5e6be',
                      color:
                        deliveryMethod === 'delivery' ? '#2c2100' : '#8c742a',
                      border:
                        deliveryMethod === 'delivery'
                          ? '1.4px solid #bca75e'
                          : '1px solid #eadcb3',
                      fontSize: '.9em',
                    }}
                  >
                    Delivery
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeliveryMethod('collection')}
                    className="btn btn-sm fw-bold flex-grow-1"
                    style={{
                      borderRadius: 999,
                      background:
                        deliveryMethod === 'collection'
                          ? 'linear-gradient(90deg,#d4c4a8,#c4a15a)'
                          : '#f5e6be',
                      color:
                        deliveryMethod === 'collection'
                          ? '#2c2100'
                          : '#8c742a',
                      border:
                        deliveryMethod === 'collection'
                          ? '1.4px solid #bca75e'
                          : '1px solid #eadcb3',
                      fontSize: '.9em',
                    }}
                  >
                    Collection (Free)
                  </button>
                </div>
                <div
                  className="mt-1"
                  style={{ fontSize: '.86em', color: '#7a6430' }}
                >
                  {deliveryMethod === 'collection'
                    ? 'Collect from Zoteya Beauty Studio in Sandton. No delivery fee applies.'
                    : 'Door-to-door courier delivery. Free for orders from R5,200, otherwise R200.'}
                </div>
              </div>

              <div
                className="border-top pt-3 mt-2"
                style={{ borderColor: '#f5e4b8' }}
              >
                <div
                  className="fw-semibold"
                  style={{ fontSize: '.98em', color: '#2c2c2c' }}
                >
                  Subtotal: R {subtotal.toLocaleString()}
                </div>
                <div style={{ fontSize: '.96em' }}>
                  Shipping:{' '}
                  <span
                    style={{
                      color: shipping === 0 ? '#1a8500' : '#2c2c2c',
                    }}
                  >
                    R {shipping.toLocaleString()}
                  </span>
                </div>
                <div
                  className="fw-bold mt-2"
                  style={{ color: '#2c2c2c', fontSize: '1.04em' }}
                >
                  Total: R {total.toLocaleString()}
                </div>
              </div>

              <button
                onClick={() => navigate('/')}
                className="btn btn-lg fw-bold rounded-pill mt-3 w-100"
                style={{
                  background: 'linear-gradient(90deg,#d4c4a8,#c4a15a)',
                  color: '#2c2100',
                  fontSize: '.98rem',
                  border: 'none',
                }}
              >
                Continue Shopping
              </button>

              {cart.length > 0 && (
                <button
                  onClick={clearCart}
                  className="btn btn-danger fw-bold rounded-pill mt-2 w-100"
                  style={{
                    fontSize: '.97em',
                    border: 'none',
                    background: '#bb2d3b',
                    color: '#fff',
                    boxShadow: '0 1.5px 7px #c18f8f28',
                  }}
                >
                  Clear Cart
                </button>
              )}
            </div>
          </div>

          {/* LEFT: Delivery form via StaticForms (AJAX) */}
          <div className="col-12 col-md-7 order-2 order-md-2">
            {deliveryMethod === 'delivery' && (
            <div
              className="card border-0 shadow-sm rounded-4 p-3 mb-4"
              style={{ background: '#ffffff' }}
            >
              <div
                className="d-flex align-items-center gap-2 p-2 mb-2 rounded-3"
                style={{
                  background: '#ffffff',
                  border: '1px solid #e0e0e0',
                  fontSize: '.97rem',
                }}
              >
                <span style={{ fontSize: '1.1em' }}>📦</span>
                <span
                  className="text-muted"
                  style={{ color: '#6b4b13', fontWeight: 500 }}
                >
                  <strong>Note:</strong> Hair orders delivered within{' '}
                  <strong>7-14 working days</strong>.
                </span>
              </div>

              <h2
                className="mb-3 ps-1 fw-bold"
                style={{
                  color: '#2c2c2c',
                  fontFamily: "'Playfair Display', serif",
                  borderBottom: '1.5px solid #e0e0e0',
                  fontSize: '1.13rem',
                  paddingBottom: '.22em',
                }}
              >
                Delivery Info
              </h2>

              <form
                ref={formRef}
                method="POST"
                className="row gy-2 px-1"
                onSubmit={handleFormSubmit}
              >
                {/* StaticForms API key */}
                <input
                  type="hidden"
                  name="apiKey"
                  value="sf_26i181e3ffnn0i4ame94e499"
                />

                {/* Use customer's email as Reply-To for your client */}
                <input type="hidden" name="replyTo" value="@email" />

                {/* cart summary */}
                <input
                  type="hidden"
                  name="cart_contents"
                  ref={cartInputRef}
                />

                {/* delivery method for StaticForms */}
                <input
                  type="hidden"
                  name="delivery_method"
                  value={deliveryMethod}
                />

                <div className="col-12">
                  <input
                    name="name"
                    placeholder="Full Name"
                    required
                    className="form-control form-control-sm border-beige"
                    style={{ fontSize: '.97em' }}
                  />
                </div>
                <div className="col-12">
                  <input
                    name="email"
                    placeholder="Email Address"
                    required
                    type="email"
                    className="form-control form-control-sm border-beige"
                    style={{ fontSize: '.97em' }}
                  />
                </div>
                <div className="col-12">
                  <input
                    name="phone"
                    placeholder="Phone Number"
                    required
                    className="form-control form-control-sm border-beige"
                    style={{ fontSize: '.97em' }}
                  />
                </div>
                <div className="col-12">
                  <input
                    name="street_address"
                    placeholder="Street Address"
                    required
                    className="form-control form-control-sm border-beige"
                    style={{ fontSize: '.97em' }}
                  />
                </div>
                <div className="col-12">
                  <input
                    name="address_line_2"
                    placeholder="Address Line 2 (optional)"
                    className="form-control form-control-sm border-beige"
                    style={{ fontSize: '.97em' }}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    name="city"
                    placeholder="City"
                    required
                    className="form-control form-control-sm border-beige"
                    style={{ fontSize: '.97em' }}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    name="province"
                    placeholder="Province"
                    required
                    className="form-control form-control-sm border-beige"
                    style={{ fontSize: '.97em' }}
                  />
                </div>
                <div className="col-12">
                  <input
                    name="postal_code"
                    placeholder="Postal Code"
                    required
                    className="form-control form-control-sm border-beige"
                    style={{ fontSize: '.97em' }}
                  />
                </div>
                <div className="col-12">
                  <textarea
                    name="notes"
                    placeholder="Additional Notes (optional)"
                    className="form-control border-beige"
                    style={{ fontSize: '.96em' }}
                  ></textarea>
                </div>

                <div className="col-12">
                  <button
                    type="submit"
                    className="btn fw-bold w-100 rounded-pill py-2 mt-2"
                    style={{
                      background:
                        'linear-gradient(90deg,#d4c4a8,#c4a15a)',
                      color: '#232323',
                      fontSize: '.99em',
                      border: 'none',
                    }}
                  >
                    Submit Delivery Info
                  </button>
                </div>
                <div
                  className="col-12 text-secondary text-center mt-1"
                  style={{ fontSize: '.93em' }}
                >
                  After submitting, complete your order by paying below.
                </div>

                {success && (
                  <div
                    className="col-12 text-center mt-2"
                    style={{
                      fontSize: '.93em',
                      color: '#1a8500',
                      fontWeight: 500,
                    }}
                  >
                    Thank you! Your delivery details have been received.
                  </div>
                )}
              </form>

              {/* Colour add-on side note */}
              <div
                className="mt-3 p-3 rounded-3"
                style={{
                  background: '#ffffff',
                  border: '1px dashed #e0e0e0',
                  fontSize: '.93rem',
                  color: '#6b4b13',
                }}
              >
                <strong>Colour add‑on notes:</strong> Balayage, solid colour
                and highlights can be added as extra colour services in‑salon
                after your booking is confirmed. Pricing and maintenance will be
                discussed during your colour consultation.
              </div>

              {/* Terms & Policies acceptance */}
              <div
                id="terms-checkbox-wrapper"
                className="mt-3"
                style={{ fontSize: '.9rem', color: '#2c2c2c' }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => {
                      setAcceptedTerms(e.target.checked);
                      if (e.target.checked) setShowTermsError(false);
                    }}
                    style={{ marginTop: '3px' }}
                  />
                  <span>
                    I have read and agree to Zoteya Beauty’s{' '}
                    <a
                      href="/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms &amp; Conditions
                    </a>
                    ,{' '}
                    <a
                      href="/shipping-returns"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Shipping &amp; Returns Policy
                    </a>{' '}
                    and{' '}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </span>
                </label>
                {showTermsError && !acceptedTerms && (
                  <div
                    style={{
                      color: '#b3261e',
                      marginTop: '4px',
                      fontSize: '.86rem',
                    }}
                  >
                    Please accept the terms and policies before proceeding to
                    payment.
                  </div>
                )}
              </div>

              <a
                href={`https://pay.yoco.com/zoteya-beauty-studio?amount=${total}`}
                onClick={handlePayNowClick}
                target="_blank"
                rel="noopener noreferrer"
                className="btn fw-bold rounded-pill w-100 mt-3 shadow"
                style={{
                  background: 'linear-gradient(90deg,#cab27c,#e7d5a2 100%)',
                  color: '#564600',
                  fontSize: '1.02em',
                  border: 'none',
                  padding: '10px 0',
                  pointerEvents: cart.length === 0 || !success ? 'none' : 'auto',
                  opacity: cart.length === 0 || !success ? 0.6 : 1,
                }}
              >
                Pay Now
              </a>
            </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CheckoutPage;